// Data Sources page for OpenAI: one tab per connected ChatGPT workspace or API org.
import { useCallback, useEffect, useRef, useState } from "react";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import {
    createOpenAiIntegration,
    deleteOpenAiIntegration,
    replaceOpenAiKey,
} from "../../lib/openaiClient.js";
import { NO_DIRECTORY, openaiProblem, openaiSteps, scopeCaptureStep, whenLabel } from "./model.js";
import { AsideSection, SourceLoadState, SourcePage, Step, Steps, Tabs, inputCls } from "../../components/setupSteps.jsx";
import { TONE } from "../../components/statusCard.jsx";
import Button from "../../components/ui/Button.jsx";
import Field from "../../components/ui/Field.jsx";
import { ErrorText, SaveStatus } from "../../components/ui/Notice.jsx";

const NEW_TAB = "new";

function workspaceTone(integration) {
    const [, , sync] = openaiSteps(integration);
    if (sync === "failed") return TONE.ATTENTION;
    if (sync !== "done") return TONE.WAITING;
    return TONE.OK;
}

function KeyField({ value, onChange }) {
    return (
        <Field label="Compliance Logs Platform key" hint="Stored encrypted and never shown again.">
            <input
                type="password"
                value={value}
                autoComplete="off"
                onChange={(event) => onChange(event.target.value)}
                placeholder="Compliance Logs Platform key"
                className={inputCls}
            />
        </Field>
    );
}

function ConnectSteps({ onCreated }) {
    const caps = useMyPermissions();
    const [apiKey, setApiKey] = useState("");
    const [principalId, setPrincipalId] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const typed = apiKey.trim().length > 0;

    const create = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await createOpenAiIntegration({ apiKey: apiKey.trim(), principalId: principalId.trim() });
            setApiKey("");
            setPrincipalId("");
            await onCreated();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [apiKey, caps, onCreated, principalId]);

    const canCreate = typed && principalId.trim().length > 0 && !saving;

    return (
        <Steps label="Connect an OpenAI workspace">
            <Step
                number={1}
                state={typed ? "done" : "current"}
                title="Create a Compliance Logs Platform key"
                summary="Create one in the OpenAI admin console for the workspace or organization you want captured, and note its ID."
            />
            <Step
                number={2}
                state={typed ? "current" : "todo"}
                title="Paste the key and workspace ID"
                summary="A key reads one workspace or organization. Connect each one separately."
            >
                <div className="grid gap-[14px]">
                    <KeyField value={apiKey} onChange={setApiKey} />
                    <Field
                        label="Workspace or organization ID"
                        hint="Your ChatGPT workspace ID, or an org-... ID for an API-Platform organization."
                    >
                        <input
                            type="text"
                            value={principalId}
                            autoComplete="off"
                            spellCheck={false}
                            onChange={(event) => setPrincipalId(event.target.value)}
                            placeholder="org-... or a workspace ID"
                            className={inputCls}
                        />
                    </Field>
                    <div className="flex flex-wrap items-center justify-end gap-[8px]">
                        <SaveStatus error={error} />
                        <Button variant="primary" onClick={create} disabled={!canCreate}>
                            {saving ? "Connecting…" : "Connect workspace"}
                        </Button>
                    </div>
                </div>
            </Step>
            <Step
                number={3}
                state="todo"
                title="Confirm the first sync"
                summary="Origin reads new activity about once a minute."
                last
            />
        </Steps>
    );
}

function WorkspaceSteps({ integration, directory, onChanged, refreshing }) {
    const caps = useMyPermissions();
    const {
        states: [, , syncState],
        noDirectory,
    } = scopeCaptureStep(openaiSteps(integration), directory);
    const [replacing, setReplacing] = useState(false);
    const [apiKey, setApiKey] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const lastPoll = whenLabel(integration.lastPollAt);

    const replace = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await replaceOpenAiKey(integration.id, apiKey.trim());
            setApiKey("");
            setReplacing(false);
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [apiKey, caps, integration.id, onChanged]);

    const cancel = useCallback(() => {
        setReplacing(false);
        setApiKey("");
        setError(null);
    }, []);

    return (
        <Steps label={`OpenAI workspace ${integration.principalId}`}>
            <Step number={1} state="done" title="Create a Compliance Logs Platform key" summary="Done." />
            <Step
                number={2}
                state={replacing ? "current" : "done"}
                title="Key saved"
                summary={
                    <>
                        Reading <span className="font-mono">{integration.principalId}</span>.
                    </>
                }
                action={
                    !replacing ? <Button onClick={() => setReplacing(true)}>Replace key</Button> : null
                }
            >
                {replacing ? (
                    <div className="grid gap-[14px]">
                        <KeyField value={apiKey} onChange={setApiKey} />
                        <div className="flex flex-wrap items-center justify-end gap-[8px]">
                            <SaveStatus error={error} />
                            <Button onClick={cancel}>Cancel</Button>
                            <Button variant="primary" onClick={replace} disabled={apiKey.trim().length === 0 || saving}>
                                {saving ? "Saving…" : "Replace key"}
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Step>
            <Step
                number={3}
                state={syncState}
                title={noDirectory ? NO_DIRECTORY.label : syncState === "done" ? "Capturing" : "Confirm the first sync"}
                summary={
                    noDirectory
                        ? NO_DIRECTORY.detail
                        : syncState === "failed"
                          ? openaiProblem(integration)
                          : syncState === "done"
                            ? `Last sync ${lastPoll}.`
                            : "Waiting for the first sync. Refresh to check again."
                }
                action={
                    !noDirectory && syncState !== "done" ? (
                        <Button onClick={onChanged} disabled={refreshing}>
                            {refreshing ? "Checking…" : "Refresh"}
                        </Button>
                    ) : null
                }
                last
            />
        </Steps>
    );
}

export default function OpenAiPage({
    source,
    health,
    entry,
    directory,
    onBack,
    onChanged,
    selectedItem,
    onSelectItem,
}) {
    const caps = useMyPermissions();
    const integrations = entry?.data ?? null;
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState(null);
    const knownIds = useRef(null);

    const selected =
        integrations && integrations.some((integration) => integration.id === selectedItem)
            ? selectedItem
            : integrations && integrations.length > 0 && selectedItem !== NEW_TAB
              ? integrations[0].id
              : NEW_TAB;
    const current = integrations?.find((integration) => integration.id === selected) ?? null;

    const created = useCallback(async () => {
        knownIds.current = new Set((integrations ?? []).map((integration) => integration.id));
        await onChanged();
    }, [integrations, onChanged]);

    useEffect(() => {
        if (!knownIds.current || !integrations) return;
        const added = integrations.find((integration) => !knownIds.current.has(integration.id));
        knownIds.current = null;
        if (added) onSelectItem(added.id);
    }, [integrations, onSelectItem]);

    const remove = useCallback(async () => {
        const target = confirmRemove;
        setConfirmRemove(null);
        if (!target) return;
        setRemoving(true);
        setError(null);
        try {
            await deleteOpenAiIntegration(target.id);
            onSelectItem(null);
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setRemoving(false);
        }
    }, [caps, confirmRemove, onChanged, onSelectItem]);

    if (!integrations) {
        return (
            <SourcePage source={source} health={health} onBack={onBack} backLabel="Back to data sources" busy={!entry?.loaded || entry?.loading} aside={null}>
                <SourceLoadState entry={entry} onRetry={onChanged} />
            </SourcePage>
        );
    }

    const tabs =
        integrations.length > 0 ? (
            <Tabs
                label="OpenAI workspaces"
                selected={selected}
                onSelect={onSelectItem}
                items={[
                    ...integrations.map((integration) => ({
                        id: integration.id,
                        label: integration.principalId,
                        mono: true,
                        dotTone: workspaceTone(integration),
                    })),
                    { id: NEW_TAB, label: "+ Connect workspace" },
                ]}
            />
        ) : null;

    return (
        <SourcePage
            source={source}
            health={health}
            onBack={onBack}
            backLabel="Back to data sources"
            busy={entry.loading}
            tabs={tabs}
            aside={
                <>
                    <AsideSection title="What Origin does">
                        <p className="m-0">
                            Reads ChatGPT conversations, app and MCP calls, and Codex sessions through OpenAI's
                            Enterprise Compliance Logs Platform.
                        </p>
                        <p className="m-0">Shows them alongside the prompts captured on endpoints.</p>
                    </AsideSection>
                    <AsideSection title="One key, one workspace">
                        <p className="m-0">Connect each ChatGPT workspace or API organization you want captured.</p>
                    </AsideSection>
                    {current ? (
                        <AsideSection title="Disconnect">
                            <p className="m-0">Capture stops for this workspace. Activity already imported is kept.</p>
                            <Button variant="danger" onClick={() => setConfirmRemove(current)} disabled={removing} className="w-fit">
                                Disconnect
                            </Button>
                            <ErrorText>{error}</ErrorText>
                        </AsideSection>
                    ) : null}
                </>
            }
        >
            {current ? (
                <WorkspaceSteps
                    key={current.id}
                    integration={current}
                    directory={directory}
                    onChanged={onChanged}
                    refreshing={entry.loading}
                />
            ) : (
                <ConnectSteps onCreated={created} />
            )}
            <ConfirmDialog
                open={Boolean(confirmRemove)}
                analyticsId={DIALOG.OPENAI_KEY_REMOVE}
                eyebrow="OpenAI"
                title="Disconnect this workspace?"
                body="Capture stops for this workspace immediately. Activity already imported is kept. Reconnecting means pasting a key again."
                confirmLabel="Disconnect"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(null)}
            />
        </SourcePage>
    );
}
